import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useAuthStore } from "@/stores/authStore";
import type { User, TelegramUser } from "@/types";
import type { TelegramThemeParams } from "@/integrations/telegram/telegramService";

// Re-export types for backward compatibility
export type { AuthStatus } from "@/types";

// Unified context interface that combines both Auth and Telegram
interface UnifiedAuthContextType {
  // Auth state
  user: User | null;
  status: 'loading' | 'authenticated' | 'unauthenticated';
  loading: boolean;
  error: string | null;
  isRecoveryMode: boolean;

  // Telegram state
  isTelegram: boolean;
  telegramUser: TelegramUser | null;
  isInitialized: boolean;
  isAuthenticating: boolean;
  currentTheme: TelegramThemeParams | null;
  isThemeApplied: boolean;
  colorScheme: 'light' | 'dark' | null;
  authContext: 'web' | 'telegram';
  debugLog: string[];

  // Auth actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<{ error: Error | null }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  updateEmail: (newEmail: string) => Promise<{ error: Error | null }>;
  refreshSession: () => Promise<{ error: Error | null }>;
  handleRecoveryFlow: () => Promise<void>;
  clearRecoveryMode: () => void;
  signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>;

  // Telegram actions
  authenticateWithTelegram: () => Promise<void>;
  setError: (error: string | null) => void;
}

// Create unified context
const UnifiedAuthContext = createContext<UnifiedAuthContextType | null>(null);

// Main hook for unified auth
export const useUnifiedAuth = () => {
  const context = useContext(UnifiedAuthContext);
  if (!context) {
    throw new Error("useUnifiedAuth must be used within UnifiedAuthProvider");
  }
  return context;
};

// Backward compatibility hooks
export const useAuth = () => {
  const unified = useUnifiedAuth();
  // Return only auth-related fields for backward compatibility
  return {
    user: unified.user,
    status: unified.status,
    loading: unified.loading,
    error: unified.error,
    isRecoveryMode: unified.isRecoveryMode,
    login: unified.login,
    register: unified.register,
    logout: unified.logout,
    resetPassword: unified.resetPassword,
    updatePassword: unified.updatePassword,
    updateEmail: unified.updateEmail,
    refreshSession: unified.refreshSession,
    handleRecoveryFlow: unified.handleRecoveryFlow,
    clearRecoveryMode: unified.clearRecoveryMode,
    signInWithMagicLink: unified.signInWithMagicLink,
  };
};

export const useTelegram = () => {
  const unified = useUnifiedAuth();
  // Return only telegram-related fields for backward compatibility
  return {
    isTelegram: unified.isTelegram,
    telegramUser: unified.telegramUser,
    isInitialized: unified.isInitialized,
    isAuthenticating: unified.isAuthenticating,
    currentTheme: unified.currentTheme,
    isThemeApplied: unified.isThemeApplied,
    colorScheme: unified.colorScheme,
  };
};

// Unified Provider that replaces both AuthProvider and TelegramProvider
export const UnifiedAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const store = useAuthStore();

  // Initialize auth on mount
  useEffect(() => {
    store.initialize();
  }, []);

  // Create the full context value
  const contextValue = useMemo<UnifiedAuthContextType>(() => ({
    // Auth state
    user: store.user,
    status: store.status,
    loading: store.loading,
    error: store.error,
    isRecoveryMode: store.isRecoveryMode,

    // Telegram state
    isTelegram: store.isTelegram,
    telegramUser: store.telegramUser,
    isInitialized: store.isInitialized,
    isAuthenticating: store.isAuthenticating,
    currentTheme: store.currentTheme,
    isThemeApplied: store.isThemeApplied,
    colorScheme: store.colorScheme,
    authContext: store.authContext,
    debugLog: store.debugLog,

    // Auth actions
    login: store.login,
    register: store.register,
    logout: store.logout,
    resetPassword: store.resetPassword,
    updatePassword: store.updatePassword,
    updateEmail: store.updateEmail,
    refreshSession: store.refreshSession,
    handleRecoveryFlow: store.handleRecoveryFlow,
    clearRecoveryMode: store.clearRecoveryMode,
    signInWithMagicLink: store.signInWithMagicLink,

    // Telegram actions
    authenticateWithTelegram: store.authenticateWithTelegram,
    setError: store.setError,
  }), [
    store.user,
    store.status,
    store.loading,
    store.error,
    store.isRecoveryMode,
    store.isTelegram,
    store.telegramUser,
    store.isInitialized,
    store.isAuthenticating,
    store.currentTheme,
    store.isThemeApplied,
    store.colorScheme,
    store.authContext,
    store.debugLog,
    store.login,
    store.register,
    store.logout,
    store.resetPassword,
    store.updatePassword,
    store.updateEmail,
    store.refreshSession,
    store.handleRecoveryFlow,
    store.clearRecoveryMode,
    store.signInWithMagicLink,
    store.authenticateWithTelegram,
    store.setError
  ]);

  return (
    <UnifiedAuthContext.Provider value={contextValue}>
      {children}
    </UnifiedAuthContext.Provider>
  );
};

// Export backward-compatible providers that just pass through children
// These are deprecated and should be removed in future versions
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  console.warn('AuthProvider is deprecated. Use UnifiedAuthProvider instead.');
  return <>{children}</>;
};

export const TelegramProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  console.warn('TelegramProvider is deprecated. Use UnifiedAuthProvider instead.');
  return <>{children}</>;
};